import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Offer } from './offers.model';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class OffersService {
  constructor(private http: HttpClient) {}

  /**
   * Get offers with search, pagination, and ERP-standard filters
   * (storeId, status, offer type, active-on date range).
   *
   * Filters that are empty / null / undefined are simply not sent so
   * the backend can fall back to its default "no filter" branch.
   */
  getOffers(
    pageOptions: { page: number; limit: number },
    searchTerm: string,
    filters: {
      storeIds?: string[];
      status?: string;
      type?: string;
      startDate?: string;
      endDate?: string;
    } = {},
  ): Observable<{ items: Offer[]; total: number }> {
    let params = new HttpParams()
      .set('page', pageOptions.page)
      .set('limit', pageOptions.limit);
    if (searchTerm) params = params.set('query', searchTerm);
    if (filters.type) params = params.set('type', filters.type);
    if (filters.status) params = params.set('status', filters.status);
    if (filters.storeIds && filters.storeIds.length) {
      params = params.set('storeId', filters.storeIds.join(','));
    }
    if (filters.startDate) params = params.set('startDate', filters.startDate);
    if (filters.endDate) params = params.set('endDate', filters.endDate);
    return this.http.get<{ items: Offer[]; total: number }>(
      `${environment.offers}`, {params}
    );
  }

  /**
   * Fetch a single offer by ID with ALL relations fully enriched
   * (offerVariants/exclusions/freeItems carry variant display data +
   * pricing, plus offerStores/brand/category/department id lists).
   *
   * Used by the Edit modal so it can hydrate every picker with real
   * product names instead of relying on the partial list row. The list
   * row (findAll) is a summary; this is the detail fetch.
   */
  getOffer(id: string): Observable<Offer> {
    return this.http.get<Offer>(`${environment.offers}/${id}`);
  }

  /**
   * Add a new offer
   */
  addOffer(offer: Offer): Observable<Offer> {
    return this.http.post<Offer>(`${environment.offers}`, offer);
  }

  /**
   * Update Offer - if you need edit functionality later
   */
  updateOffer(id: string, offer: Offer): Observable<Offer> {
    return this.http.patch<Offer>(`${environment.offers}/${id}`, offer);
  }

  /**
   * Delete Offer - if you need delete functionality
   */
  deleteOffer(id: string): Observable<any> {
    return this.http.delete(`${environment.offers}/${id}`);
  }

  // ── MRP-off-list upload ──────────────────────────────────────────────
  // Sheet of SKU / MRP / "Offer Price - For All Stores" / "Offer Price -
  // <store code>" columns. Preview is a dry run (row errors + the offers
  // that would be created); upload re-sends the same file and creates them
  // all-or-nothing. Form fields travel in the same multipart body.
  downloadMrpOffListTemplate(offerType: 'PriceOffOnProduct' | 'PercentOffOnMRP'): Observable<Blob> {
    return this.http.get(`${environment.offers}/upload/template`, {
      params: new HttpParams().set('offerType', offerType),
      responseType: 'blob',
    });
  }

  previewMrpOffList(formData: FormData): Observable<any> {
    return this.http.post(`${environment.offers}/upload/preview`, formData);
  }

  uploadMrpOffList(formData: FormData): Observable<any> {
    return this.http.post(`${environment.offers}/upload`, formData);
  }

  /**
   * Stores for the per-store offer picker. Same shape used by /terminals.
   * Kept local to OffersService so the offers feature doesn't depend on
   * the terminals module just to read a dropdown.
   */
  getStores(): Observable<any> {
    return this.http.get(environment.stores);
  }

  /**
   * Rich product search for the offer's free-item picker. Hits the same
   * /products/search endpoint used by GRN / PO / MBQ, which matches on
   * longDescription (the full marketing name), sku, and variant barcode.
   *
   * Context = PRODUCT_LIST so the result set is the full catalog
   * (active products only) without the stricter filters BILLING /
   * GRN / PO apply — the admin is setting an offer up in advance and
   * shouldn't be restricted by "currently in stock at this terminal".
   *
   * Response shape: { data: Product[], total, ... } where each Product
   * carries a nested variants[] array — caller flattens to variants.
   */
  searchProductsForOffer(query: string): Observable<any> {
    const params = new HttpParams()
      .set('query', query || '')
      .set('context', 'PRODUCT_LIST')
      .set('page', '1')
      .set('limit', '20');
    return this.http.get(`${environment.apiUrl}/products/search`, { params });
  }
}
